/**
 * Fuente única de verdad de las provincias españolas (ISO 3166-2:ES).
 *
 * Consumidores: apps/checkout (selector del paso 2 y desglose del carrito),
 * packages/payments (metadata del customer de Stripe) y libs/checkout
 * (facturación en Holded).
 *
 * Este fichero NO importa nada a propósito: viaja al bundle de cliente.
 * La lógica fiscal vive en ./tax-region, no aquí.
 *
 * Nota: CE (Ceuta) y ML (Melilla) son ciudades autónomas, no provincias, pero
 * se mantienen porque Stripe las devuelve en address.state.
 */
export const SPAIN_PROVINCE_NAMES = {
  A: 'Alicante',
  AB: 'Albacete',
  AL: 'Almería',
  AV: 'Ávila',
  B: 'Barcelona',
  BA: 'Badajoz',
  BI: 'Vizcaya',
  BU: 'Burgos',
  C: 'A Coruña',
  CA: 'Cádiz',
  CC: 'Cáceres',
  CE: 'Ceuta',
  CO: 'Córdoba',
  CR: 'Ciudad Real',
  CS: 'Castellón',
  CU: 'Cuenca',
  GC: 'Las Palmas',
  GI: 'Girona',
  GR: 'Granada',
  GU: 'Guadalajara',
  H: 'Huelva',
  HU: 'Huesca',
  J: 'Jaén',
  L: 'Lleida',
  LE: 'León',
  LO: 'La Rioja',
  LU: 'Lugo',
  M: 'Madrid',
  MA: 'Málaga',
  ML: 'Melilla',
  MU: 'Murcia',
  NA: 'Navarra',
  O: 'Asturias',
  OR: 'Ourense',
  P: 'Palencia',
  PM: 'Baleares',
  PO: 'Pontevedra',
  S: 'Cantabria',
  SA: 'Salamanca',
  SE: 'Sevilla',
  SG: 'Segovia',
  SO: 'Soria',
  SS: 'Guipúzcoa',
  T: 'Tarragona',
  TE: 'Teruel',
  TF: 'Santa Cruz de Tenerife',
  TO: 'Toledo',
  V: 'Valencia',
  VA: 'Valladolid',
  VI: 'Álava',
  Z: 'Zaragoza',
  ZA: 'Zamora',
} as const

export type SpainProvinceCode = keyof typeof SPAIN_PROVINCE_NAMES

export type SpainProvince = {
  readonly code: SpainProvinceCode
  readonly name: string
}

/** Lista ordenada por nombre (es-ES) para pintar el selector del paso 2. */
export const SPAIN_PROVINCES: readonly SpainProvince[] = (
  Object.keys(SPAIN_PROVINCE_NAMES) as SpainProvinceCode[]
)
  .map((code) => ({ code, name: SPAIN_PROVINCE_NAMES[code] }))
  .sort((a, b) => a.name.localeCompare(b.name, 'es-ES'))

export const isSpainProvinceCode = (
  value: unknown
): value is SpainProvinceCode =>
  typeof value === 'string' &&
  Object.prototype.hasOwnProperty.call(SPAIN_PROVINCE_NAMES, value)

/**
 * Normaliza lo que llega del formulario o de Stripe a un código ISO.
 * Acepta 'gc', ' GC ' y la forma larga 'ES-GC'. Devuelve null si no casa.
 */
export const normalizeSpainProvinceCode = (
  value: string | null | undefined
): SpainProvinceCode | null => {
  if (!value) return null
  const code = value.trim().toUpperCase().replace(/^ES-/, '')
  return isSpainProvinceCode(code) ? code : null
}

const normalizeName = (value: string): string =>
  value
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')

/** Nombres alternativos que la gente y Stripe escriben. Claves ya normalizadas. */
const SPAIN_PROVINCE_NAME_ALIASES: Record<string, SpainProvinceCode> = {
  TENERIFE: 'TF',
  'SC TENERIFE': 'TF',
  'GRAN CANARIA': 'GC',
  'LAS PALMAS DE GRAN CANARIA': 'GC',
  'ISLAS BALEARES': 'PM',
  'ILLES BALEARS': 'PM',
  'LA CORUNA': 'C',
  BIZKAIA: 'BI',
  GIPUZKOA: 'SS',
  ARABA: 'VI',
}

const NAME_TO_CODE: Record<string, SpainProvinceCode> = (
  Object.keys(SPAIN_PROVINCE_NAMES) as SpainProvinceCode[]
).reduce(
  (acc, code) => {
    acc[normalizeName(SPAIN_PROVINCE_NAMES[code])] = code
    return acc
  },
  { ...SPAIN_PROVINCE_NAME_ALIASES } as Record<string, SpainProvinceCode>
)

/**
 * Búsqueda inversa por nombre. Hace falta porque para cuando el dato llega a
 * facturación ya se ha convertido el código en nombre ('Las Palmas'), y sin
 * esto un canario acabaría facturado al 21%.
 */
export const findSpainProvinceCodeByName = (
  value: string | null | undefined
): SpainProvinceCode | null => {
  if (!value) return null
  return NAME_TO_CODE[normalizeName(value)] ?? null
}

/** Código ISO a partir de un código o de un nombre, en un solo paso. */
export const toSpainProvinceCode = (
  value: string | null | undefined
): SpainProvinceCode | null =>
  normalizeSpainProvinceCode(value) ?? findSpainProvinceCodeByName(value)

export const getSpainProvinceName = (
  value: string | null | undefined
): string | null => {
  const code = toSpainProvinceCode(value)
  return code ? SPAIN_PROVINCE_NAMES[code] : null
}
